import { useContext } from "react";

import { isEmpty } from "@swiggy-private/js-utils";
import { IPLWidgetControllerCards } from "@swiggy-private/dls-react-mxn/dist/WidgetController/PLWidgetController/type";
import type { ILocalCart } from "@swiggy-private/portal-dineout-context";
import {
    QrCodeActions,
    ResponseContext,
    SwiggyContextHeader,
    TableContext,
    useDigitalMenuConfig,
    usePlApiContextFetch,
} from "@swiggy-private/portal-dineout-context";

import { useNativeCartSync } from "../components/CartOrders/CartTab/useNativeCartSync";
import { API_ENDPOINTS, DINEOUT_CART_API_HOST, GANDALF_API_HOST, LOG_TABLES, STORAGE_KEYS } from "../constants";
import { getDefaultApiHeaders } from "../utils/api";
import { filterAvailableCartItems, persistLocalCart } from "../utils/fetchCartHelpers";

export const useUpdateCart = () => {
    const fetchPL = usePlApiContextFetch();
    const { swiggyHeaders } = useContext(SwiggyContextHeader);
    const { tableId = "", restaurantId = "", parentOrderId = "" } = useContext(TableContext);
    const { dispatch } = useContext(ResponseContext);
    const { config } = useDigitalMenuConfig();
    const useDineoutCartHost = config?.useDineoutCartHostCartPage ?? false;
    const { updateLocalCartInNative, clearLocalCartInNative, refreshMenuInNative } = useNativeCartSync();

    const updateCart = async (payload: Record<string, unknown>) => {
        if (!swiggyHeaders) {
            return Promise.reject(new Error("Missing swiggyHeaders"));
        }

        const apiHost = useDineoutCartHost ? DINEOUT_CART_API_HOST : GANDALF_API_HOST;
        const endpoint = useDineoutCartHost ? API_ENDPOINTS.UPDATE_CART_DINEOUT : API_ENDPOINTS.UPDATE_CART;

        try {
            const updatedPayload = {
                ...payload,
                restaurantId: Number(restaurantId),
                tableId,
                parentOrderId,
            };

            const response = await fetchPL?.<{ cards: IPLWidgetControllerCards[] }>(
                {
                    apiHost,
                    endpoint,
                    bodyParams: updatedPayload,
                    options: {
                        method: "POST",
                        headers: { ...swiggyHeaders, ...getDefaultApiHeaders(swiggyHeaders) } as HeadersInit,
                    },
                },
                { ...swiggyHeaders, ...getDefaultApiHeaders(swiggyHeaders) },
                () => {},
                LOG_TABLES,
            );

            if (!isEmpty(response) && response?.success) {
                dispatch({
                    type: QrCodeActions.CART_UPDATE_SUCCESS,
                    payload: { response },
                });

                if (useDineoutCartHost) {
                    const responseCards = response.success.cards || [];
                    const synced = filterAvailableCartItems(responseCards, updatedPayload as unknown as ILocalCart);
                    dispatch({ type: QrCodeActions.UPDATE_LOCAL_CART, payload: synced });
                    persistLocalCart(synced, responseCards, {
                        updateLocalCartInNative,
                        clearLocalCartInNative,
                        refreshMenuInNative,
                    });
                } else {
                    dispatch({ type: QrCodeActions.UPDATE_LOCAL_CART, payload: updatedPayload });
                    try {
                        sessionStorage.setItem(STORAGE_KEYS.LOCAL_CART, JSON.stringify(updatedPayload));
                    } catch (_) {}
                }
            }
        } catch (_error) {
            dispatch({
                type: QrCodeActions.CART_UPDATE_ERROR,
                payload: {},
            });
        }
    };

    return { updateCart };
};
